import { ActionIcon, Anchor, Group, Table, Text, Tooltip } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link, useLocation } from 'react-router'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { SortableLabel } from '@/components/ui-shared/Tables/SortableLabel/SortableLabel'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { CameraListItem } from '@/types/camera'
import { CrudAction, Resource } from '@/types/permissions'
import { SortConfig } from '@/utils/sorting'
import { CameraSourceBadge } from '../CameraBadges/CameraSourceBadge'
import { CameraTypeBadge } from '../CameraBadges/CameraTypeBadge'
import { DeleteCameraModal } from '../DeleteCameraModal'
import {
  CameraListFilter,
  CameraListFilterConfig
} from './CameraListFilter/CameraListFilter'
import { CameraThumbnail } from './CameraThumbnail'

export type TableSortConfig = SortConfig<'camera_name'>

type CameraListProps = {
  cameras: CameraListItem[]
  sortConfig: TableSortConfig
  filterConfig: CameraListFilterConfig
  isLoading?: boolean
  hasError?: boolean
  onSort: (sortConfig: TableSortConfig) => void
  onFilter: (filterConfig: CameraListFilterConfig) => void
  onReload?: () => void
}

export const CameraList = ({
  cameras,
  sortConfig,
  filterConfig,
  isLoading,
  hasError,
  onFilter,
  onSort,
  onReload
}: CameraListProps) => {
  const [canDeleteCamera] = useHasPermissions(Resource.Cameras, [
    CrudAction.Delete
  ])

  const [cameraToDelete, setCameraToDelete] = useState<CameraListItem | null>(
    null
  )

  const location = useLocation()

  const hasActiveFilter = Object.values(filterConfig).some(Boolean)

  // Cameras in ThingsBoard are linked to devices, and the device ID is required to retrieve camera details.
  // We pass the camera's device ID as a query parameter when navigating to the camera details screen.
  const getCameraDetailsPath = (camera: CameraListItem) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('cameraDeviceId', camera.device_id)

    return {
      pathname: camera.camera_id,
      search: searchParams.toString()
    }
  }

  return (
    <>
      <DataTable
        filters={
          (cameras.length > 0 || hasActiveFilter) && (
            <CameraListFilter
              filterConfig={filterConfig}
              onFilterChange={onFilter}
            />
          )
        }
        hasItems={cameras.length > 0}
        isLoading={isLoading}
        hasError={hasError}
        hasActiveFilter={hasActiveFilter}
        emptyMessage={<FormattedMessage id="cameras.list.empty" />}
        onReload={onReload}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={100}>&nbsp;</Table.Th>

            <Table.Th w={320}>
              <SortableLabel
                label={<FormattedMessage id="cameras.fields.name" />}
                sortKey="camera_name"
                disabled={cameras.length === 0}
                sortConfig={sortConfig}
                onSort={onSort}
              />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="cameras.fields.status" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="cameras.fields.type" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="cameras.fields.device" />
            </Table.Th>

            <Table.Th>&nbsp;</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {cameras.map((camera) => (
            <Table.Tr key={camera.camera_id}>
              <Table.Td>
                <CameraThumbnail
                  imageUrl={camera.thumbnail}
                  to={getCameraDetailsPath(camera)}
                />
              </Table.Td>

              <Table.Td>
                <Anchor
                  component={Link}
                  size="sm"
                  underline="hover"
                  c="blue.8"
                  fw="bold"
                  to={getCameraDetailsPath(camera)}
                >
                  {camera.camera_name}
                </Anchor>
              </Table.Td>

              <Table.Td>
                <OnlineStatus
                  status={camera.connected ? 'online' : 'offline'}
                />
              </Table.Td>

              <Table.Td>
                <Group gap={4}>
                  <CameraTypeBadge type={camera.stream_type} />
                  <CameraSourceBadge source={camera.source} />
                </Group>
              </Table.Td>

              <Table.Td>
                <Text size="xs">{camera.device_name}</Text>
              </Table.Td>

              <Table.Td>
                <Group wrap="nowrap" gap={4} justify="end">
                  {canDeleteCamera && (
                    <Tooltip label={<FormattedMessage id="delete" />}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        radius="xl"
                        onClick={() => setCameraToDelete(camera)}
                      >
                        <IconTrash style={{ width: '70%', height: '70%' }} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DataTable>

      <DeleteCameraModal
        opened={!!cameraToDelete}
        camera={cameraToDelete || undefined}
        onClose={() => setCameraToDelete(null)}
        onDelete={() => {
          setCameraToDelete(null)
          void onReload?.()
        }}
      />
    </>
  )
}
